import * as Sentry from '@sentry/vue';
import type { App } from 'vue';
import type { Router } from 'vue-router';

type InitVueSentryParams = {
  app: App;
  router?: Router;
  /**
   * Optional logical name to distinguish multiple Vue apps in one project.
   * Useful for searching/filtering inside Sentry/GlitchTip.
   */
  appName?: string;
};

function pickDsn(): string | undefined {
  // Prefer HTML-provided value so the DSN can live in Rails credentials.
  const meta = document.querySelector('meta[name="sentry-dsn"]') as
    | HTMLMetaElement
    | null;
  const metaDsn = meta?.content?.trim();
  if (metaDsn) return metaDsn;

  // Allow local overrides / CI via Vite env.
  const viteDsn = (import.meta.env.VITE_SENTRY_DSN as string | undefined)?.trim();
  if (viteDsn) return viteDsn;

  return undefined;
}

function pickEnvironment(): string {
  // Prefer explicit override (useful for future "staging" without code changes).
  const envFromVite =
    (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) ?? undefined;
  if (envFromVite) return envFromVite;

  // Optional: allow a meta tag override without bundler config.
  // (e.g. Rails layout can render <meta name="sentry-environment" content="<%= Rails.env %>">)
  const meta = document.querySelector('meta[name="sentry-environment"]') as
    | HTMLMetaElement
    | null;
  if (meta?.content) return meta.content;

  // Vite guarantees MODE:
  // - `vite dev` => "development"
  // - `vite build` => "production"
  // - `vite --mode <name>` => "<name>"
  const mode = import.meta.env.MODE;
  if (mode === 'production') return 'production';
  if (mode === 'development') return 'development';
  return mode || (import.meta.env.PROD ? 'production' : 'development');
}

function pickRelease(): string | undefined {
  // Prefer a build-time value if you have it (CI can set it).
  const envRelease = (import.meta.env.VITE_RELEASE as string | undefined) ?? undefined;
  if (envRelease) return envRelease;

  // Optional: allow a meta tag override without bundler config.
  const meta = document.querySelector('meta[name="app-release"]') as
    | HTMLMetaElement
    | null;
  return meta?.content || undefined;
}

export function initVueSentry({ app, router, appName }: InitVueSentryParams) {
  const dsn = pickDsn();
  if (!dsn) {
    // No DSN configured => Sentry disabled for this build/page.
    return;
  }

  // Router-based performance tracing (only for apps that use vue-router).
  const integrations = router
    ? [Sentry.browserTracingIntegration({ router })]
    : undefined;

  Sentry.init({
    app,
    dsn,
    integrations,
    tracesSampleRate: Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? 0.01),
    environment: pickEnvironment(),
    release: pickRelease(),
    ...(appName ? { initialScope: { tags: { vue_app: appName } } } : {}),
    beforeSend(event, hint) {
      const error = hint.originalException;
      // Do not report request aborted by navigation (full-page reload, tab close).
      // Browser aborts in-flight XHR → Axios throws "Network Error" with no response.
      if (error && typeof error === 'object') {
        const err = error as { message?: string; code?: string; response?: unknown };
        if (err.code === 'ERR_CANCELED') return null;
        if (err.message === 'Network Error' && err.response == null) return null;
      }
      return event;
    },
  });
}
